import {
  faFlask,
  faPlus,
  faSmog,
  faTags,
  faTimesCircle,
  faTrash,
  faClipboardCheck,
  faFileExport,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { UIView, type UIViewInjectedProps } from '@uirouter/react';
import { Button, Dropdown, Menu, Modal, Space } from 'antd';
import type { TableRowSelection } from 'antd/lib/table/interface';
import { observer } from 'mobx-react-lite';
import { useState, useCallback, useEffect, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { api } from '@/api';
import { Table } from '@/components/table';
import { getGenericFilters, toQueryFormat } from '@/components/table/utils';
import { PERMISSION_CREATE, PERMISSION_OBJ_LOT, PERMISSION_DELETE, PERMISSION_UPDATE } from '@/constants/permissions';
import { withPermissions } from '@/hocs/permission';
import { lotStore, settingsStore } from '@/stores';
import { showError } from '@/utils/common';

import { columns, draftColumns } from './setup';
import { getTranslatedString } from '@/utils';
import { faFileExcel, faPlusCircle } from '@fortawesome/pro-solid-svg-icons';
import { Tabs } from 'antd';

const { TabPane } = Tabs;

const store = lotStore;
const { confirm } = Modal;

const ToolbarButton = ({ onClick, i18nTitle, disabled, icon }: any) => {
  return (
    <Button onClick={onClick} type="primary" icon={icon} disabled={disabled}>
      &nbsp;&nbsp;
      <FormattedMessage id={i18nTitle} />
    </Button>
  );
};

const CreateButton = withPermissions([[PERMISSION_OBJ_LOT, PERMISSION_CREATE]])(ToolbarButton);
const UpdateButton = withPermissions([[PERMISSION_OBJ_LOT, PERMISSION_UPDATE]])(ToolbarButton);

const AddLotsButton = withPermissions([[PERMISSION_OBJ_LOT, PERMISSION_CREATE]])(({ onClick, overlay }: any) => {
  return (
    <Dropdown.Button
      type="primary"
      placement="bottomCenter"
      onClick={onClick}
      overlay={overlay}
      icon={<FontAwesomeIcon icon={faPlus} />}
    >
      <FormattedMessage id="lots.mass-add-new" />
    </Dropdown.Button>
  );
});

const MassDeleteButton = withPermissions([[PERMISSION_OBJ_LOT, PERMISSION_DELETE]])(
  ({ onClick, i18nTitle, disabled }: any) => {
    return (
      <Button
        onClick={onClick}
        type="primary"
        danger={true}
        disabled={disabled}
        icon={<FontAwesomeIcon icon={faTrash} />}
      >
        &nbsp;&nbsp;
        <FormattedMessage id={i18nTitle} />
      </Button>
    );
  },
);

export const Lots = observer(({ transition }: UIViewInjectedProps) => {
  const [selectedLots, setSelectedLots] = useState<number[]>([]);
  const [isDraftFilter, setIsDraftFilter] = useState<boolean>(false);
  // AntD Table keeps column filter/sort in its own state; bump this key to remount
  // and clear it on tab switch / Clear-all-filters.
  const [tableResetKey, setTableResetKey] = useState(0);
  // Whether any column filter is set — drives the Clear-all-filters disabled state.
  // Maintained from onChange since the filter state is internal to AntD Table.
  const [hasTableFilters, setHasTableFilters] = useState(false);

  const lots = store.items;

  const fillStore = useCallback(
    (params: any) => {
      store.loading = true;
      return api.lots
        .getAll({ limit: settingsStore.itemsPerPage, ...params, isDraft: isDraftFilter })
        .source.then(({ data }) => store.fillStore(data.items, data.count))
        .catch(showError)
        .finally(() => {
          store.loading = false;
        });
    },
    [isDraftFilter],
  );

  useEffect(() => {
    fillStore({});
  }, [fillStore]);

  const onFilterChange = (activeKey: string) => {
    setIsDraftFilter(activeKey === 'draft');
    setTableResetKey((k) => k + 1);
    setHasTableFilters(false);
  };

  const genericFilters = useMemo(() => getGenericFilters(isDraftFilter ? draftColumns : columns), [isDraftFilter]);

  const onChangeTable = (...args: any[]) => {
    // args = [pagination, filters, sorter, extra]. Track whether any column filter is active so the
    // Clear-all-filters button can disable when there is nothing to clear.
    const filters = args[1] as Record<string, any> | undefined;
    setHasTableFilters(filters ? Object.values(filters).some((v) => Array.isArray(v) && v.length > 0) : false);
    fillStore(toQueryFormat.apply(null, [genericFilters, ...args]));
  };

  const onMassAdd = () => {
    transition.router.stateService.go('base-layout.lots.mass-create');
  };

  const onMassAddProduct = () => {
    transition.router.stateService.go('base-layout.lots.mass-add-product', { selectedLots });
  };

  const onPullReport = () => {
    transition.router.stateService.go('base-layout.lots.export', { selectedLots });
  };

  const onMassAddPledge = () => {
    transition.router.stateService.go('base-layout.lots.mass-create-pledge', { selectedLots });
  };

  const onMassAddFumigation = () => {
    transition.router.stateService.go('base-layout.lots.mass-create-fumigation', { selectedLots });
  };

  const onMassAddBV = () => {
    transition.router.stateService.go('base-layout.lots.mass-create-bv', { selectedLots });
  };

  const onCreateFromExcel = () => {
    transition.router.stateService.go(`base-layout.lots.create-from-excel`);
  };

  const onLinkLotsWaybills = () => {
    transition.router.stateService.go(`base-layout.lots.link-lots-waybills`);
  };

  const onClearAllFilters = useCallback(() => {
    setTableResetKey((k) => k + 1);
    setHasTableFilters(false);
    fillStore({});
  }, [fillStore]);

  const onMassDelete = () => {
    const onOk = () =>
      api.lots
        .deleteMany({ ids: selectedLots })
        .source.then(() =>
          selectedLots.forEach((id: number) => {
            lotStore.deleteItem(id);
          }),
        )
        .then(() => fillStore({}))
        .then(() => setSelectedLots([]))
        .catch(showError);

    confirm({
      title: getTranslatedString('lots.delete-warning-title'),
      content: getTranslatedString('lots.delete-warning-desc'),
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk,
    });
  };

  const onRow = (record: cyrian.api.lots.Lot) => ({
    onClick: () => transition.router.stateService.go('base-layout.lots.view', { lotId: record.id }),
    className: 'table-row-cursor',
  });

  const onRowSelectionChange = (selectedRowKeys: number[], selectedLots: cyrian.api.lots.Lot[]) => {
    setSelectedLots(selectedRowKeys);
  };

  const rowSelection: TableRowSelection<cyrian.api.lots.Lot> = {
    preserveSelectedRowKeys: true,
    selectedRowKeys: selectedLots,
    onChange: onRowSelectionChange,
  };

  const tableColumns = isDraftFilter ? draftColumns : columns;
  // On "Empty lots only" tab, show only draft lots so a lot saved with isDraft: false disappears without refetch
  const displayedLots = isDraftFilter ? lots.filter((l: any) => l.isDraft === true) : lots;

  const importMenu = (
    <Menu>
      <Menu.Item key="1" icon={<FontAwesomeIcon icon={faPlusCircle} />} onClick={onLinkLotsWaybills}>
        <FormattedMessage id="lots.link-lots-waybills" />
      </Menu.Item>
      <Menu.Item key="2" icon={<FontAwesomeIcon icon={faFileExcel} />} onClick={onCreateFromExcel}>
        <FormattedMessage id="lots.create-from-excel" />
      </Menu.Item>
    </Menu>
  );

  return (
    <>
      <Tabs activeKey={isDraftFilter ? 'draft' : 'all'} onChange={onFilterChange} style={{ marginBottom: 16 }}>
        <TabPane tab={<FormattedMessage id="lots.filter-all" />} key="all" />
        <TabPane tab={<FormattedMessage id="lots.filter-empty-only" />} key="draft" />
      </Tabs>
      <Space
        size={0}
        wrap
        align="center"
        style={{ display: 'flex', width: '100%', marginBottom: 16, justifyContent: 'space-between' }}
      >
        <Space wrap>
          {!isDraftFilter && (
            <>
              <AddLotsButton onClick={onMassAdd} overlay={importMenu} />
              <UpdateButton
                onClick={onMassAddProduct}
                i18nTitle="lots.associate-product"
                disabled={!selectedLots.length}
                icon={<FontAwesomeIcon icon={faTags} />}
              />
              <CreateButton
                onClick={onMassAddBV}
                i18nTitle="lots.mass-add-bv"
                disabled={!selectedLots.length}
                icon={<FontAwesomeIcon icon={faClipboardCheck} />}
              />
              <CreateButton
                onClick={onMassAddFumigation}
                i18nTitle="lots.mass-add-fumigation"
                disabled={!selectedLots.length}
                icon={<FontAwesomeIcon icon={faSmog} />}
              />
              <CreateButton
                onClick={onMassAddPledge}
                i18nTitle="lots.mass-add-pledges"
                disabled={!selectedLots.length}
                icon={<FontAwesomeIcon icon={faFlask} />}
              />
              <ToolbarButton
                onClick={onPullReport}
                i18nTitle="lots.pull-report"
                disabled={!selectedLots.length}
                icon={<FontAwesomeIcon icon={faFileExport} />}
              />
            </>
          )}
          <MassDeleteButton onClick={onMassDelete} i18nTitle="lots.mass-remove" disabled={!selectedLots.length} />
        </Space>
        {!isDraftFilter && (
          <Button
            type="default"
            icon={<FontAwesomeIcon icon={faTimesCircle} />}
            onMouseDown={(e) => e.preventDefault()}
            onClick={onClearAllFilters}
            disabled={!hasTableFilters}
          >
            &nbsp;&nbsp;
            <FormattedMessage id="lots.clearAllFilters" />
          </Button>
        )}
      </Space>
      <Table
        key={tableResetKey}
        loading={store.loading}
        rowKey="id"
        columns={tableColumns}
        dataSource={[...displayedLots]}
        fillStore={fillStore}
        store={store}
        onRow={onRow}
        onChange={onChangeTable}
        rowSelection={rowSelection}
        scroll={{ x: isDraftFilter ? 400 : 1800 }}
      />
      <UIView />
    </>
  );
});
